// The quote document: the SOW as structured data rather than prose in a
// textarea, authored here instead of in Qwilr.
//
// Types AND arithmetic live in shared on purpose. The editor shows a
// running total while the agent types, the server recomputes it on save
// and writes it onto the quote ticket, and the client-facing page and
// PDF will render it again later. Every one of those is a chance to
// round differently or to disagree about whether a 'months' line counts
// as hours. One implementation, imported by all of them, is the reason
// they cannot — the same argument as RUNAWAY_TIMER_MINUTES.
/** The units offered in the picker. Not a limit - anything may be typed. */
export const QUOTE_UNIT_SUGGESTIONS = [
    'hours',
    'each',
    'users',
    'mailboxes',
    'devices',
    'sites',
    'months',
    'flat rate',
];
/** The one unit with arithmetic behind it. */
export const HOURS_UNIT = 'hours';
export function isHoursUnit(unit) {
    return unit.trim().toLowerCase() === HOURS_UNIT;
}
/**
 * How deep a list goes: 1 (1. / •), 2 (a. / ◦), 3 (i. / ▪).
 *
 * `levels` runs parallel to `items` and is ABSENT on a flat list, which
 * is every list written before nesting existed - so their stored JSON,
 * and the hash of any snapshot holding one, is unchanged. The MSA
 * summary is the reason it exists: flattened, its lettered and roman
 * sub-items were numbered 1 to 17 straight through.
 */
export const MAX_LIST_LEVEL = 3;
/** The level of each item, 1 where none is recorded. */
export function listLevels(block) {
    return block.items.map((_, i) => Math.min(MAX_LIST_LEVEL, Math.max(1, Math.floor(block.levels?.[i] ?? 1))));
}
/**
 * The marker each item is drawn with. Numbering restarts under each
 * parent, as a contract numbers its clauses: 1. a. i. ii. b. 2.
 */
export function listMarkers(block) {
    const levels = listLevels(block);
    if (block.type === 'bullets')
        return levels.map((l) => ['•', '◦', '▪'][l - 1]);
    const counters = [0, 0, 0];
    return levels.map((level) => {
        counters[level - 1] += 1;
        for (let deeper = level; deeper < MAX_LIST_LEVEL; deeper++)
            counters[deeper] = 0;
        const n = counters[level - 1];
        if (level === 1)
            return `${n}.`;
        if (level === 2)
            return `${String.fromCharCode(96 + ((n - 1) % 26) + 1)}.`;
        return `${toRoman(n)}.`;
    });
}
function toRoman(n) {
    const table = [
        [10, 'x'], [9, 'ix'], [5, 'v'], [4, 'iv'], [1, 'i'],
    ];
    let out = '';
    let rest = n;
    for (const [value, numeral] of table) {
        while (rest >= value) {
            out += numeral;
            rest -= value;
        }
    }
    return out;
}
export const QUOTE_BLOCK_TYPES = [
    'paragraph',
    'subheading',
    'bullets',
    'numbered',
];
export const QUOTE_BLOCK_LABELS = {
    paragraph: 'Paragraph',
    subheading: 'Subheading',
    bullets: 'Bulleted list',
    numbered: 'Numbered list',
};
/**
 * House style: a plain hyphen, never an em or en dash.
 *
 * Applied on save rather than refused, because the text that trips it is
 * almost always pasted from Word or from an older Qwilr page, and
 * rejecting a whole document over a dash someone did not type is hostile.
 * Swapping the character changes nothing about what the sentence says,
 * which is why normalising is safe here and would not be for, say, a
 * price.
 */
export function normalizeHouseText(text) {
    return text.replace(/[—–]/g, '-');
}
export function blockIsEmpty(block) {
    if (block.type === 'bullets' || block.type === 'numbered') {
        return block.items.every((i) => i.trim() === '');
    }
    return block.text.trim() === '';
}
/**
 * Blocks flattened to plain text.
 *
 * This is what reaches `sow_summary` on the ticket and from there the
 * project, so it is deliberately plain and deterministic: list markers
 * are literal so a technician reading the project ticket still sees a
 * list, and the same blocks always render the same string.
 */
export function renderBlocks(blocks) {
    const parts = [];
    for (const block of blocks) {
        if (blockIsEmpty(block))
            continue;
        switch (block.type) {
            case 'paragraph':
                parts.push(stripEmphasis(block.text.trim()));
                break;
            case 'subheading':
                parts.push(stripEmphasis(block.text.trim()));
                break;
            case 'bullets':
            case 'numbered': {
                // Nested items are indented under their parent and keep the
                // marker the client sees, so a technician reading the project
                // ticket reads "a." where the summary says "a.".
                const levels = listLevels(block);
                const markers = listMarkers(block);
                parts.push(block.items
                    .map((item, i) => ({ item: item.trim(), level: levels[i], marker: markers[i] }))
                    .filter((x) => x.item)
                    .map((x) => `${'   '.repeat(x.level - 1)}${block.type === 'bullets' ? '-' : x.marker} ${stripEmphasis(x.item)}`)
                    .join('\n'));
                break;
            }
        }
    }
    return parts.join('\n\n');
}
// --- emphasis -------------------------------------------------------------
//
// One inline mark and only one: **bold**. The house copy leans on it
// ("The **problem** we have seen...", "**T3 TECH PARTNERS LLC**" in the
// agreements) and a page without it does not read like the one clients
// were sent. It stays plain text in storage - the asterisks are part of
// the string that gets hashed - so a block is still four types and no
// markup, and every renderer agrees what it says.
// The inside may not contain an asterisk or a line break, so a divider
// of stars ("******", which the agreements use) stays literal text.
const EMPHASIS = /\*\*([^*\n]+?)\*\*/g;
/** The text with its emphasis markers removed, for plain-text renderings. */
export function stripEmphasis(text) {
    return text.replace(EMPHASIS, '$1');
}
/** The text split into runs, for renderers that can draw bold. */
export function emphasisRuns(text) {
    const runs = [];
    let last = 0;
    for (const match of text.matchAll(EMPHASIS)) {
        const at = match.index ?? 0;
        if (at > last)
            runs.push({ text: text.slice(last, at), bold: false });
        runs.push({ text: match[1], bold: true });
        last = at + match[0].length;
    }
    if (last < text.length)
        runs.push({ text: text.slice(last), bold: false });
    return runs;
}
// --- the client as a party to the agreements ------------------------------
//
// The MNDA and the MSA name the client and its registered address in
// their opening paragraph. A template cannot know either, so it carries
// these two tokens and the quote fills them FROM THE ORGANIZATION RECORD
// when it is sent - into the frozen snapshot, so the hash covers the
// name the client actually signed under.
//
// Filled at send, not when the template is applied: an address corrected
// on the client record after the quote was started must still reach the
// agreement, and a document still being written should show the agent
// where the client's details will go.
export const CLIENT_NAME_TOKEN = '[CLIENT NAME]';
export const CLIENT_ADDRESS_TOKEN = '[CLIENT ADDRESS]';
/**
 * An organization's address as the agreements print it:
 * "20545 Center Ridge Rd. Suite #420, Rocky River, Ohio 44116".
 *
 * Country is left off for the United States, which is how every
 * agreement T3 has sent writes a US address, and included otherwise.
 * Null when there is no street line - a city alone is not an address
 * anyone can serve notice at.
 */
export function formatPartyAddress(parts) {
    const clean = (v) => (v ?? '').trim();
    const street = [clean(parts.line1), clean(parts.line2)].filter(Boolean).join(' ');
    if (!street)
        return null;
    const regionPostal = [clean(parts.region), clean(parts.postalCode)].filter(Boolean).join(' ');
    const country = clean(parts.country);
    const domestic = /^(us|usa|u\.s\.a?\.?|united states( of america)?)$/i.test(country);
    return [street, clean(parts.city), regionPostal, domestic ? '' : country]
        .filter(Boolean)
        .join(', ');
}
/** Whether any text in the document uses `token`. */
export function documentUsesToken(doc, token) {
    const texts = [doc.title, doc.intro ?? '', doc.closing ?? ''];
    for (const section of doc.sections) {
        texts.push(section.heading, section.body ?? '');
        for (const block of section.blocks ?? []) {
            if (block.type === 'bullets' || block.type === 'numbered')
                texts.push(...block.items);
            else
                texts.push(block.text);
        }
    }
    return texts.some((t) => t.includes(token));
}
/**
 * Put the client into the text. A missing address leaves its token in
 * place rather than blanking it, so an unfinished agreement LOOKS
 * unfinished - the send route refuses that case before it gets here.
 */
export function fillClientText(text, party) {
    let out = text.split(CLIENT_NAME_TOKEN).join(party.name);
    if (party.address)
        out = out.split(CLIENT_ADDRESS_TOKEN).join(party.address);
    return out;
}
export function fillClientBlocks(blocks, party) {
    return blocks.map((block) => {
        if (block.type === 'bullets' || block.type === 'numbered') {
            return { ...block, items: block.items.map((i) => fillClientText(i, party)) };
        }
        return { type: block.type, text: fillClientText(block.text, party) };
    });
}
/**
 * Money to the cent. Quantities and prices arrive as JSON numbers, so
 * `0.1 * 3` style drift is real; every amount in the app rounds here and
 * nowhere else.
 */
export function roundMoney(value) {
    return Math.round((value + Number.EPSILON) * 100) / 100;
}
export function lineItemAmount(item) {
    return roundMoney(item.quantity * item.unitPriceUsd);
}
export function quoteTotals(lineItems) {
    let hours = 0;
    let amountUsd = 0;
    for (const item of lineItems) {
        if (isHoursUnit(item.unit))
            hours += item.quantity;
        amountUsd += lineItemAmount(item);
    }
    // Hours are quoted in quarter-hours at worst; two decimals matches the
    // numeric(10,2) the column stores, so the UI total and the stored one
    // agree exactly.
    return { hours: roundMoney(hours), amountUsd: roundMoney(amountUsd) };
}
/**
 * Split a total into the deposit taken at signature and the balance owed
 * on completion.
 *
 * Works in CENTS and derives the balance by SUBTRACTION, so the two
 * always add back to exactly the total. Halving an odd number of cents
 * and rounding both halves independently is how a client ends up billed
 * a cent more than they agreed to.
 *
 * Lives here, next to quoteTotals, for the same reason that does: the
 * editor shows the client what they will be charged and the server
 * charges it, and those two numbers may never be computed differently.
 */
export function splitDeposit(totalCents, depositPercent) {
    const depositCents = Math.round((totalCents * depositPercent) / 100);
    return { depositCents, balanceCents: totalCents - depositCents };
}
/**
 * The cap on `sow_summary` in the quote ticket's custom fields
 * (ticketRules.QUOTE_FIELDS). The document renders down into that field
 * on every save, so a document whose rendered text exceeds this is
 * rejected at save time with a message saying so — rather than being
 * silently truncated, which would file a clipped SOW against the work.
 */
export const SOW_SUMMARY_MAX_CHARS = 20_000;
/**
 * The document flattened to the plain text that lands on the quote
 * ticket's `sow_summary`, and from there on the project's. This is the
 * projection that makes the document reach the project ticket without
 * anyone retyping it, so it is deliberately plain and deterministic:
 * the same document always renders the same string.
 *
 * ROLLED-UP SECTIONS ARE LEFT OUT. They are the agreements - the MNDA and
 * MSA run to about 59,000 characters - and they are terms, not scope: a
 * technician working the project needs what was sold, not the contract
 * it was sold under. Carrying them also pushed every block-hours quote
 * past SOW_SUMMARY_MAX_CHARS, so the editor refused to save one at all.
 * They remain in the document, the snapshot and the hash.
 */
export function renderSowSummary(doc) {
    const parts = [];
    if (doc.intro && doc.intro.trim())
        parts.push(doc.intro.trim());
    for (const section of doc.sections) {
        if (section.collapsible)
            continue;
        const body = renderBlocks(section.blocks);
        parts.push(body ? `${section.heading.trim()}\n${body}` : section.heading.trim());
    }
    if (doc.closing && doc.closing.trim())
        parts.push(doc.closing.trim());
    return parts.join('\n\n');
}
/**
 * Resolve a client's plan choice against the plans they were actually
 * shown.
 *
 * THE BROWSER SENDS AN ID AND NOTHING ELSE. The price, the hours and the
 * name all come from the frozen snapshot, so a page that posts a $5
 * "20 Hours" plan resolves to the real one or to nothing. Same rule as
 * verifying a payment with Stripe rather than believing the page: the
 * client is not a source of truth about what they were offered.
 */
export function resolveSelectedPlan(plans, planId) {
    if (!planId)
        return null;
    const plan = plans.find((p) => p.id === planId);
    if (!plan)
        return null;
    return {
        id: plan.id,
        name: plan.name,
        hours: plan.hours,
        amountCents: Math.round(plan.monthlyPriceUsd * 100),
    };
}
/** The last day of the month (ET) on which a client may start now. */
export const BLOCK_HOURS_START_NOW_LAST_DAY = 14;
/**
 * The 1st of the current month when starting now is offered on `todayIso`
 * (`YYYY-MM-DD`, ET), else null. One function for the page and the server.
 */
export function blockHoursStartNowOn(todayIso) {
    const day = Number(todayIso.slice(8, 10));
    return day >= 1 && day <= BLOCK_HOURS_START_NOW_LAST_DAY ? `${todayIso.slice(0, 7)}-01` : null;
}
/** Whether this document makes the client choose before they can sign. */
export const requiresPlanSelection = (plans) => plans.length > 0;
/**
 * The consent the signer ticks. Kept here rather than in the page so the
 * exact words that were agreed to live beside the record of agreeing —
 * if this text ever changes, the change is visible in version control
 * next to the column that stores the answer.
 */
export const ESIGN_CONSENT_TEXT = 'I agree that typing my name below is my electronic signature, and that it is as legally binding as a handwritten one. I am authorised to accept this quote on behalf of my organisation.';
// --- editing a section as plain text --------------------------------------
//
// Blocks are how a section is STORED (so every renderer agrees how to
// draw it) and a terrible way to EDIT one: a stack of little boxes with
// type dropdowns is not how anyone writes a paragraph of scope.
//
// So the editor shows a section as one piece of text and parses it back.
// The markers are the ones people already type - "- " for a bullet,
// "1. " for a numbered step, "## " for a subheading - and the two
// functions are exact inverses, so opening a section and closing it
// without touching anything changes nothing.
const SUBHEADING_MARKER = /^##\s+/;
const BULLET_MARKER = /^[-*]\s+/;
const NUMBERED_MARKER = /^\d+[.)]\s+/;
/** Blocks as the text a person edits. Inverse of `textToBlocks`. */
export function blocksToText(blocks) {
    const parts = [];
    for (const block of blocks) {
        switch (block.type) {
            case 'subheading':
                parts.push(`## ${block.text.trim()}`);
                break;
            case 'paragraph':
                // A paragraph whose line LOOKS like a marker is escaped, or it
                // would come back as a list: "8. No Obligation. Nothing in this
                // Agreement..." is clause 8 of the MNDA, and parsed as a numbered
                // item it would be renumbered 1. on the next save.
                parts.push(block.text
                    .trim()
                    .split('\n')
                    .map((line) => (LOOKS_MARKED.test(line.trim()) ? `\\${line.trim()}` : line.trim()))
                    .join('\n'));
                break;
            case 'bullets':
            case 'numbered': {
                const levels = listLevels(block);
                parts.push(block.items
                    .map((item, n) => {
                    const indent = LEVEL_INDENT.repeat(levels[n] - 1);
                    return `${indent}${block.type === 'bullets' ? '-' : `${n + 1}.`} ${item.trim()}`;
                })
                    .join('\n'));
                break;
            }
        }
    }
    return parts.join('\n\n');
}
/** Three spaces of indent per list level in the editor's text. */
const LEVEL_INDENT = '   ';
/** A line that the parser would read as something other than paragraph text. */
const LOOKS_MARKED = /^(##\s|[-*]\s|\d+[.)]\s|\\)/;
/** An escaped paragraph line: a leading backslash, removed on the way back. */
const ESCAPED = /^\\/;
function levelOf(rawLine) {
    const indent = rawLine.match(/^[ \t]*/)[0].replace(/\t/g, LEVEL_INDENT).length;
    return Math.min(MAX_LIST_LEVEL, 1 + Math.round(indent / LEVEL_INDENT.length));
}
/** Append an item to the open list of this type, or open one. */
function pushListItem(blocks, type, item, level) {
    const last = blocks[blocks.length - 1];
    const list = last && last.type === type ? last : (blocks.push({ type, items: [] }), blocks[blocks.length - 1]);
    const open = list;
    const levels = listLevels(open);
    open.items.push(item);
    levels.push(level);
    // Recorded only when something is nested, so a flat list stays exactly
    // the shape it has always been stored in.
    if (levels.some((l) => l > 1))
        open.levels = levels;
    else
        delete open.levels;
}
/**
 * Text back into blocks. Inverse of `blocksToText`.
 *
 * Consecutive marked lines of the same kind become ONE list, and a run of
 * unmarked lines becomes one paragraph — which is what the text looks
 * like to the person who typed it. A blank line ends whatever is open.
 *
 * A single line break inside a paragraph is KEPT. It used to be folded
 * into a space, which ran the notice addresses in the agreements together
 * ("T3 Tech Partners LLC50 Catoctin Cir"). A hard break someone typed is
 * a break they meant.
 *
 * An indented marker nests the item under the one above it (three spaces
 * a level, up to three levels), and a leading backslash keeps a line
 * that looks like a marker as paragraph text.
 */
export function textToBlocks(text) {
    const blocks = [];
    let paragraph = [];
    const flushParagraph = () => {
        const joined = paragraph.join('\n').trim();
        if (joined)
            blocks.push({ type: 'paragraph', text: joined });
        paragraph = [];
    };
    for (const rawLine of text.split('\n')) {
        const line = rawLine.trim();
        if (line === '') {
            flushParagraph();
            continue;
        }
        if (ESCAPED.test(line)) {
            paragraph.push(line.replace(ESCAPED, ''));
            continue;
        }
        if (SUBHEADING_MARKER.test(line)) {
            flushParagraph();
            blocks.push({ type: 'subheading', text: line.replace(SUBHEADING_MARKER, '').trim() });
            continue;
        }
        if (BULLET_MARKER.test(line)) {
            flushParagraph();
            pushListItem(blocks, 'bullets', line.replace(BULLET_MARKER, '').trim(), levelOf(rawLine));
            continue;
        }
        if (NUMBERED_MARKER.test(line)) {
            flushParagraph();
            pushListItem(blocks, 'numbered', line.replace(NUMBERED_MARKER, '').trim(), levelOf(rawLine));
            continue;
        }
        paragraph.push(line);
    }
    flushParagraph();
    return blocks;
}
//# sourceMappingURL=quote.js.map